import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import {
  Box,
  Flex,
  Text,
  Select,
  FormControl,
  FormLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Portal,
  Alert,
  AlertIcon,
  useDisclosure
} from '@chakra-ui/react'
import { saveAs } from 'file-saver'
import Axios from '../../../Axios'
import {
  Button,
  PeriodField,
  Loading,
  currentMonth,
  isMonth,
  money,
  get,
  writers,
  useBillingAction
} from './shared'

export default function PayrollDownload({ section }) {
  const { user } = useSelector((s) => s.auth)
  const canWrite = writers.includes(user?.role)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { busy, action } = useBillingAction()
  const cache = useQueryClient()
  useEffect(() => {
    onClose()
  }, [section, onClose])
  const [month, setMonth] = useState(currentMonth)
  const [selection, setSelection] = useState('')
  const validMonth = isMonth(month)
  const batches = useQuery(
    ['billing', 'batches', month],
    () => get('batches', { period: month }),
    { enabled: isOpen && validMonth, retry: false }
  )
  const saved = batches.data || []
  const selected =
    selection === 'new'
      ? null
      : saved.find((b) => b._id === selection) || saved[0]
  const creating = !selected
  const prepare = useQuery(
    ['billing', 'prepare', month],
    () => get('prepare', { period: month }),
    {
      enabled:
        isOpen && validMonth && creating && canWrite && batches.isSuccess,
      retry: false
    }
  )
  const issueCount = selected
    ? selected.excludedCount || 0
    : prepare.data?.issueCount || 0
  const issues = useQuery(
    ['billing', 'download-issues', month, selected?._id || 'new'],
    () =>
      selected
        ? get(`batches/${selected._id}/issues`, { limit: 50 })
        : get('prepare', { period: month, issues: true, limit: 50 }),
    {
      enabled: isOpen && issueCount > 0,
      retry: false
    }
  )
  const changeMonth = (value) => {
    setMonth(value)
    setSelection('')
  }
  const ready =
    validMonth &&
    batches.isSuccess &&
    !batches.isFetching &&
    (selected ||
      (canWrite &&
        prepare.isSuccess &&
        !prepare.isFetching &&
        !prepare.data.locked &&
        prepare.data.count > 0))
  const download = (format) =>
    action(async () => {
      let batch = selected
      if (!batch) {
        batch = (await Axios.post('/billing/batches', { period: month })).data
        // Reuse this snapshot when the other format is downloaded, even if a download fails.
        cache.setQueryData(['billing', 'batches', month], (old) => [
          batch,
          ...(old || [])
        ])
        setSelection(batch._id)
      }
      const response = await Axios.get(`/billing/batches/${batch._id}/export`, {
        params: { format },
        responseType: 'blob'
      })
      saveAs(response.data, `nomina-${month}-v${batch.version}.${format}`)
      onClose()
    })
  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      isLazy
    >
      <PopoverTrigger>
        <Button>Descargar nómina</Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="380px"
          maxW="calc(100vw - 32px)"
          borderRadius={8}
          shadow="lg"
        >
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader color="green.500" fontWeight="bold" py={3}>
            Descargar nómina
          </PopoverHeader>
          <PopoverBody p={4} maxH="75vh" overflowY="auto">
            <PeriodField
              id="payroll-month"
              value={month}
              onChange={changeMonth}
              disabled={busy}
            />
            <Text fontSize="sm" color="gray.600" mt={3}>
              Incluye los socios que corresponde cobrar en el mes seleccionado,
              desde su fecha de inscripción.
            </Text>
            <Loading query={batches} />
            {saved.length > 0 && (
              <FormControl mt={4}>
                <FormLabel
                  htmlFor="payroll-version"
                  fontSize="sm"
                  color="green.500"
                >
                  Nómina
                </FormLabel>
                <Select
                  id="payroll-version"
                  value={selection || saved[0]._id}
                  isDisabled={busy}
                  onChange={(e) => setSelection(e.target.value)}
                >
                  {saved.map((b) => (
                    <option key={b._id} value={b._id}>
                      Versión {b.version} · {b.count} socios
                    </option>
                  ))}
                  {canWrite && (
                    <option value="new">Preparar una nueva nómina</option>
                  )}
                </Select>
              </FormControl>
            )}
            {creating && canWrite && <Loading query={prepare} />}
            {(selected || (creating && prepare.isSuccess)) && (
              <Box bg="beige.500" borderRadius={8} p={3} mt={4}>
                <Text color="green.500" fontWeight="semibold">
                  {(selected?.count ?? prepare.data?.count ?? 0).toLocaleString(
                    'es-CL'
                  )}{' '}
                  socios incluidos ·{' '}
                  {money(selected?.total ?? prepare.data?.total)}
                </Text>
                {selected && (
                  <Text fontSize="xs" mt={1}>
                    Guardada el{' '}
                    {new Date(selected.createdAt).toLocaleDateString('es-CL')}.
                    Ambos formatos usan esta misma nómina.
                  </Text>
                )}
              </Box>
            )}
            {creating && !canWrite && batches.isSuccess && (
              <Text mt={4}>
                Aún no hay una nómina guardada para este mes. Solicita su
                preparación a Finanzas.
              </Text>
            )}
            {creating && prepare.data?.locked && (
              <Alert status="info" mt={4}>
                <AlertIcon />
                El mes ya tiene resultados o está cerrado. Solo se pueden
                descargar las nóminas guardadas.
              </Alert>
            )}
            {issueCount > 0 && (
              <Box mt={4}>
                <Alert
                  status="warning"
                  alignItems="flex-start"
                  borderRadius={8}
                >
                  <AlertIcon />
                  <Text fontSize="sm">
                    {issueCount}{' '}
                    {issueCount === 1
                      ? 'socio con datos pendientes queda'
                      : 'socios con datos pendientes quedan'}{' '}
                    fuera de esta nómina.{' '}
                    {(selected?.count ?? prepare.data?.count ?? 0) > 0 &&
                      'Puedes descargarla con los socios incluidos. '}
                    Completa los datos para incorporarlos en próximas nóminas.
                  </Text>
                </Alert>
                <Loading query={issues} />
                <Box maxH="140px" overflowY="auto" mt={2}>
                  {issues.data?.rows?.map((row) => (
                    <Box key={row.partner} mb={2} fontSize="sm">
                      <Link
                        to={`/partners/profile/${row.partner}/Perfil de Socio`}
                      >
                        <Text color="green.500" textDecoration="underline">
                          {row.name} · {row.rut}
                        </Text>
                      </Link>
                      <Text>{row.reason}</Text>
                    </Box>
                  ))}
                </Box>
                {issueCount > 50 && (
                  <Text fontSize="xs">Se muestran los primeros 50 casos.</Text>
                )}
              </Box>
            )}
            {creating &&
              canWrite &&
              prepare.isSuccess &&
              !prepare.data.locked && (
                <>
                  {!prepare.data.count && (
                    <Text mt={3} fontSize="sm">
                      No hay socios con datos suficientes para generar la nómina
                      de este mes.
                    </Text>
                  )}
                  <Text fontSize="xs" color="gray.600" mt={2}>
                    La primera descarga guarda la nómina del mes.
                  </Text>
                </>
              )}
            <Flex gap={3} mt={5}>
              <Button
                flex={1}
                isDisabled={!ready || busy}
                isLoading={busy}
                onClick={() => download('txt')}
              >
                TXT
              </Button>
              <Button
                flex={1}
                isDisabled={!ready || busy}
                isLoading={busy}
                onClick={() => download('csv')}
              >
                CSV para Excel
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
